/**
 * Настройки рабочего места.
 *
 * Раньше это было «Управление» — одна страница, на которую вываливались все
 * панели сразу: подключение аккаунтов, доступы, сотрудники, заготовки ответов.
 * На узком экране колонки схлопывались, и найти нужное можно было только
 * прокруткой сверху донизу.
 *
 * Теперь подвкладки. Разделение не косметическое: у панелей разная частота
 * использования и разные права. Подключение аккаунта делают раз в месяц,
 * доступы правят еженедельно, заготовки — ежедневно, и держать их на одном
 * полотне значит каждый раз проходить мимо всего остального.
 *
 * Подвкладка, на которую у сотрудника нет прав, не показывается вовсе. Это
 * UX: сервер всё равно ответит 403 на прямой запрос.
 */

import { useCallback, useEffect, useState } from "react";
import type {
  AnyTelegramAccountDto,
  UserDto,
  UserSummaryDto,
} from "@tw/shared-types";
import { type AccountFolderDto } from "../lib/api.js";
import { useSession } from "../state/session.tsx";
import { TeamsPanel } from "../components/TeamsPanel.tsx";
import { QuickRepliesPanel } from "../components/QuickRepliesPanel.tsx";
import { SectionsPanel } from "../components/SectionsPanel.tsx";
import { SectionLoginsPanel } from "../components/SectionLoginsPanel.tsx";
import { cn } from "@/lib/utils";
import { useIsMobile } from "../lib/useMedia.ts";
import { describeError } from "../lib/errors.js";
import { PeoplePanel } from "../components/PeoplePanel.tsx";
import { TelegramNodesPanel } from "../components/TelegramNodesPanel.tsx";

/**
 * Сотрудник в списке.
 *
 * Сериализатор отдаёт РАЗНЫЕ проекции в зависимости от прав зрителя: админ
 * видит полный объект, старший менеджер — урезанный. Объединение вместо
 * приведения к полному типу не даёт по ошибке обратиться к полю, которого в
 * ответе может не быть.
 */
type AnyUser = UserDto | UserSummaryDto;

type SettingsTab =
  | "accounts"
  | "logins"
  | "people"
  | "sections"
  | "replies"
  | "nodes";

export function SettingsView() {
  const mobile = useIsMobile();
  const { api, can } = useSession();

  const [accounts, setAccounts] = useState<AnyTelegramAccountDto[]>([]);
  const [users, setUsers] = useState<AnyUser[]>([]);
  const [error, setError] = useState<string | null>(null);

  const canManageUsers = can("user.create");
  const canAssign = can("account.assign.manager");
  const canAssignSenior = can("account.assign.senior");
  const canManageSections = can("section.manage");

  const [tab, setTab] = useState<SettingsTab>("accounts");

  /*
   * Папки нужны фильтру по командам и колонке «Команда».
   *
   * Грузятся здесь, а не в панели: тем же деревом пользуются переключатели
   * доступа, и два запроса за одним и тем же при переключении вкладок — это
   * ожидание там, где данные уже есть.
   */
  const [folders, setFolders] = useState<AccountFolderDto[]>([]);
  useEffect(() => {
    api
      .accountFolders()
      .then((tree) => setFolders(tree.folders))
      // Недоступное дерево не должно ломать экран: фильтр по командам просто
      // окажется пустым, а всё остальное работает.
      .catch(() => setFolders([]));
  }, [api]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [accountsRes, usersRes] = await Promise.all([
        api.listAccounts(),
        // Senior получает урезанный список (только MANAGER и INTERN) — это
        // решение сервера, клиент просто отображает то, что пришло.
        can("user.view")
          ? api.listUsers()
          : Promise.resolve({ items: [] as AnyUser[] }),
      ]);
      setAccounts(accountsRes.items);
      setUsers(usersRes.items);
    } catch (err) {
      setError(describeError(err));
    }
  }, [api, can]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Подвкладки. Набор зависит от прав: пункт, которого сотруднику не положено,
   * не показывается вовсе. Это UX — прямой запрос всё равно упрётся в 403.
   */
  const tabs: Array<{ key: SettingsTab; label: string; visible: boolean }> = [
    { key: "accounts", label: "Команды и аккаунты", visible: true },
    /*
     * Свои входы видит каждый — это личная настройка, а не управление.
     *
     * Стоит вторым пунктом: заполняют его один раз при выходе на работу, но
     * возвращаются к нему при каждой смене пароля в рабочей системе, и прятать
     * его в конец списка значило бы заставлять искать.
     */
    { key: "logins", label: "Мои системы", visible: true },
    { key: "people", label: "Сотрудники", visible: canManageUsers },
    { key: "sections", label: "Разделы", visible: canManageSections },
    {
      key: "replies",
      label: "Быстрые ответы",
      visible: can("quickreply.view"),
    },
    /*
     * Последним: серверы трогают раз в полгода, при росте числа аккаунтов, и
     * только администратор.
     */
    {
      key: "nodes",
      label: "Серверы Telegram",
      visible: can("telegram.node.manage"),
    },
  ];

  const shown = tabs.filter((t) => t.visible);
  const current = shown.some((t) => t.key === tab)
    ? tab
    : (shown[0]?.key ?? "accounts");

  return (
    <div
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden",
        !mobile && "rounded-2xl border border-border bg-card/60 shadow-md backdrop-blur-sm",
      )}
    >
      {/*
        Шапка на своей подложке и с отбивкой снизу.
        Раньше заголовок, подпись и вкладки шли одним потоком с содержимым, и
        экран читался как одна длинная простыня: глазу не за что зацепиться,
        чтобы понять, где кончается навигация и начинается работа.
      */}
      <header
        className={cn(
          "flex-none border-b border-border/70",
          mobile ? "bg-card/70 px-4 pt-1" : "bg-gradient-to-b from-secondary/50 to-transparent px-6 pb-3 pt-5",
        )}
      >
        {/* На телефоне название раздела — в шапке приложения. */}
        <div className={cn("flex flex-wrap items-baseline gap-x-3 gap-y-1", mobile && "hidden")}>
          <h1 className="text-[26px] font-semibold leading-[1.1] tracking-[-0.027em] text-heading">
            Настройки
          </h1>
          <p className="max-w-[58ch] text-[13px] leading-relaxed text-muted-foreground">
            Аккаунты компании, доступы сотрудников и разделы рабочего места.
            Любой доступ отзывается мгновенно.
          </p>
        </div>

        {/*
          Вкладки подчёркиванием, а не пилюлями.
          Пилюли внутри серой подложки спорили по весу с кнопками действий на
          самих панелях — глаз не различал, где навигация, а где то, что
          что-то делает. Подчёркивание такой путаницы не создаёт.
        */}
        <nav className={cn("flex flex-none gap-4 overflow-x-auto", mobile ? "mt-1 flex-nowrap" : "-mb-3 mt-3 flex-wrap")}>
          {shown.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-current={current === t.key ? "page" : undefined}
              className={cn(
                "relative whitespace-nowrap border-transparent bg-transparent px-0.5 pb-2.5 text-[13px] font-medium transition-colors",
                current === t.key
                  ? "text-brand after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:rounded-full after:bg-brand after:content-['']"
                  : "text-muted-foreground hover:text-heading",
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      {error && (
        <p
          className="flex-none rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {error}
        </p>
      )}

      {/* key на подвкладке: иначе React переиспользует узел и появление
          не проигрывается. */}
      <div
        key={current}
        className={cn("view-enter min-h-0 flex-1 overflow-y-auto", mobile ? "px-3 pb-4 pt-3" : "px-7 pb-7 pt-5")}
      >
        {current === "accounts" && (
          <TeamsPanel
            accounts={accounts}
            users={users}
            canAssign={canAssign}
            canAssignSenior={canAssignSenior}
            onChanged={() => void load()}
          />
        )}

        {current === "people" && canManageUsers && (
          <PeoplePanel
            users={users}
            folders={folders}
            onChanged={() => void load()}
          />
        )}

        {current === "logins" && <SectionLoginsPanel />}

        {current === "sections" && <SectionsPanel users={users} />}

        {current === "replies" && <QuickRepliesPanel users={users} />}

        {current === "nodes" && (
          <TelegramNodesPanel accounts={accounts} onChanged={() => void load()} />
        )}
      </div>
    </div>
  );
}
